import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useLists } from '../contexts/ListsContext';
import { useCollections } from '../contexts/CollectionsContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNotificationModal } from '../contexts/NotificationModalContext';
import { useTutorial } from '../contexts/TutorialContext';
import { CollectionsPicker } from '../components/EditListModal/index';
import LimitReachedModal from '../components/LimitReachedModal';
import '../styles/ListFormPage.css';

function NewList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createList } = useLists();
  const { addListToCollections } = useCollections();
  const { canAccess } = useSubscription();
  const { showAlert } = useNotificationModal();
  const { isActive: isTutorialActive, notifyAction } = useTutorial();
  const friendPickerRef = useRef(null);
  const collaboratorPickerRef = useRef(null);

  // Premium feature flag for collections
  const canAccessCollections = canAccess('collections');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    privacyStatus: 'public',
    sharedWithFriends: [],
    collaborators: [],
  });
  const [friends, setFriends] = useState([]);
  const [friendSearch, setFriendSearch] = useState('');
  const [showFriendDropdown, setShowFriendDropdown] = useState(false);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const [showCollaboratorDropdown, setShowCollaboratorDropdown] = useState(false);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [saving, setSaving] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [limitModalLimit, setLimitModalLimit] = useState(10);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState(new Set());

  const MAX_COLLABORATORS = 10;

  // Toggle collection selection
  const handleToggleCollection = (collectionId) => {
    setSelectedCollectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(collectionId)) {
        next.delete(collectionId);
      } else {
        next.add(collectionId);
      }
      return next;
    });
  };

  // Fetch friends when page loads
  useEffect(() => {
    const fetchFriends = async () => {
      if (!user) return;

      setLoadingFriends(true);
      try {
        const currentUserId = user.id || user.userId;
        const response = await api.getFriends(currentUserId);
        const friendsList = (response.data || [])
          .map(item => item.user)
          .filter(Boolean);
        setFriends(friendsList);
      } catch (err) {
        console.error('Failed to fetch friends:', err);
      } finally {
        setLoadingFriends(false);
      }
    };

    fetchFriends();
  }, [user]);


  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (friendPickerRef.current && !friendPickerRef.current.contains(event.target)) {
        setShowFriendDropdown(false);
      }
      if (collaboratorPickerRef.current && !collaboratorPickerRef.current.contains(event.target)) {
        setShowCollaboratorDropdown(false);
      }
    };

    if (showFriendDropdown || showCollaboratorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFriendDropdown, showCollaboratorDropdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'privacyStatus' && value !== 'specific-friends') {
      setFormData((prev) => ({
        ...prev,
        sharedWithFriends: [],
      }));
      setFriendSearch('');
      setShowFriendDropdown(false);
    }
  };

  const handleAddFriend = (friend) => {
    if (!formData.sharedWithFriends.includes(friend.id)) {
      setFormData((prev) => ({
        ...prev,
        sharedWithFriends: [...prev.sharedWithFriends, friend.id],
      }));
    }
    setFriendSearch('');
    setShowFriendDropdown(false);
  };

  const handleRemoveFriend = (friendId) => {
    setFormData((prev) => ({
      ...prev,
      sharedWithFriends: prev.sharedWithFriends.filter((id) => id !== friendId),
    }));
  };

  const handleAddCollaborator = (friend) => {
    if (formData.collaborators.length >= MAX_COLLABORATORS) {
      showAlert(`Maximum of ${MAX_COLLABORATORS} collaborators per list`, { variant: 'warning' });
      return;
    }
    if (!formData.collaborators.includes(friend.id)) {
      setFormData((prev) => ({
        ...prev,
        collaborators: [...prev.collaborators, friend.id],
      }));
    }
    setCollaboratorSearch('');
    setShowCollaboratorDropdown(false);
  };

  const handleRemoveCollaborator = (friendId) => {
    setFormData((prev) => ({
      ...prev,
      collaborators: prev.collaborators.filter((id) => id !== friendId),
    }));
  };

  // Filter friends based on search and exclude already selected
  const filteredFriends = friends.filter((friend) => {
    const isNotSelected = !formData.sharedWithFriends.includes(friend.id);
    const matchesSearch = friendSearch === '' ||
      (friend.displayName || '').toLowerCase().includes(friendSearch.toLowerCase()) ||
      (friend.username || '').toLowerCase().includes(friendSearch.toLowerCase());
    return isNotSelected && matchesSearch;
  });

  // Get selected friend objects for displaying pills
  const selectedFriends = friends.filter((friend) => formData.sharedWithFriends.includes(friend.id));

  // Filter friends for collaborator picker (exclude already selected as collaborators)
  const filteredCollaboratorFriends = friends.filter((friend) => {
    const isNotSelected = !formData.collaborators.includes(friend.id);
    const matchesSearch = collaboratorSearch === '' ||
      (friend.displayName || '').toLowerCase().includes(collaboratorSearch.toLowerCase()) ||
      (friend.username || '').toLowerCase().includes(collaboratorSearch.toLowerCase());
    return isNotSelected && matchesSearch;
  });

  // Get selected collaborator objects for displaying pills
  const selectedCollaborators = friends.filter((friend) => formData.collaborators.includes(friend.id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      await showAlert('Please enter a list name', { variant: 'warning' });
      return;
    }
    if (formData.privacyStatus === 'specific-friends' && formData.sharedWithFriends.length === 0) {
      await showAlert('Please select at least one friend to share with', { variant: 'warning' });
      return;
    }

    setSaving(true);

    // Map frontend privacy values to backend visibility values
    let visibility = formData.privacyStatus;
    if (formData.privacyStatus === 'friends-only') {
      visibility = 'friends_only';
    } else if (formData.privacyStatus === 'specific-friends') {
      visibility = 'shared';
    }

    const listData = {
      title: formData.name,
      description: formData.description,
      location: { display: 'Add a place to set location' },
      category: '',
      visibility: visibility,
      sharedWith: formData.sharedWithFriends,
      collaborators: formData.collaborators,
    };

    try {
      const createdList = await createList(listData);

      // Add list to selected collections (if any)
      if (selectedCollectionIds.size > 0 && canAccessCollections) {
        try {
          await addListToCollections(createdList.id, [...selectedCollectionIds]);
        } catch (err) {
          console.error('Failed to add list to collections:', err);
          // Don't block list creation on collection failure
        }
      }

      // Notify tutorial that list was created
      if (isTutorialActive) {
        notifyAction('listCreated', { listId: createdList.id });
      }
      navigate(`/list/${createdList.id}`, { replace: true });
    } catch (error) {
      console.error('Failed to create list:', error);
      // Check if this is a list limit error
      if (error.response?.data?.code === 'LIST_LIMIT_REACHED' ||
          error.message?.includes('limit') ||
          error.code === 'LIST_LIMIT_REACHED') {
        const limit = error.response?.data?.limit || error.limit || 10;
        setLimitModalLimit(limit);
        setLimitModalOpen(true);
      } else {
        showAlert('Failed to create list. Please try again.', { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/lists');
  };

  return (
    <div className="list-form-page">
      <div className="list-form-content">
        <div className="form-header">
          <button className="back-btn" onClick={handleCancel} aria-label="Go back">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="form-title">New List</h1>
          <div className="header-spacer" />
        </div>

        <form onSubmit={handleSubmit} className="list-form">
          <div className="form-group">
            <label htmlFor="name">List Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Best Coffee Shops in Brooklyn"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description <span className="optional-label">(optional)</span></label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="What types of adventures are you getting up to?"
              rows="3"
              maxLength="250"
            />
          </div>

          <div className="form-group">
            <label htmlFor="privacyStatus">Privacy</label>
            <select
              id="privacyStatus"
              name="privacyStatus"
              value={formData.privacyStatus}
              onChange={handleChange}
            >
              <option value="private">Private</option>
              <option value="public">Public</option>
              <option value="friends-only">Friends Only</option>
              <option value="specific-friends">Specific Friends</option>
            </select>
          </div>
          <p className="privacy-helper-text">You can change this anytime by editing the list</p>

          {formData.privacyStatus === 'specific-friends' && (
            <div className="form-group">
              <label>Share with</label>
              <div className="friend-picker" ref={friendPickerRef}>
                <div className="friend-picker-input-container">
                  {selectedFriends.map((friend) => (
                    <span key={friend.id} className="friend-pill">
                      {friend.displayName || friend.username}
                      <button
                        type="button"
                        className="friend-pill-remove"
                        onClick={() => handleRemoveFriend(friend.id)}
                        aria-label={`Remove ${friend.displayName || friend.username}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="friend-search-input"
                    placeholder={selectedFriends.length === 0 ? "Search friends..." : ""}
                    value={friendSearch}
                    onChange={(e) => {
                      setFriendSearch(e.target.value);
                      setShowFriendDropdown(true);
                    }}
                    onFocus={() => setShowFriendDropdown(true)}
                  />
                </div>
                {showFriendDropdown && (
                  <div className="friend-dropdown">
                    {loadingFriends ? (
                      <div className="friend-dropdown-message">Loading friends...</div>
                    ) : friends.length === 0 ? (
                      <div className="friend-dropdown-message">You don't have any friends yet.</div>
                    ) : filteredFriends.length === 0 ? (
                      <div className="friend-dropdown-message">No friends match your search</div>
                    ) : (
                      filteredFriends.map((friend) => (
                        <button
                          key={friend.id}
                          type="button"
                          className="friend-dropdown-item"
                          onClick={() => handleAddFriend(friend)}
                        >
                          <span className="friend-dropdown-name">{friend.displayName || friend.username}</span>
                          <span className="friend-dropdown-username">@{friend.username}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Collaborators Section */}
          <div className="form-group collaborator-section">
            <label>
              Collaborators{' '}
              <span className="collaborator-count">({formData.collaborators.length} / {MAX_COLLABORATORS})</span>{' '}
              <span className="optional-label">(optional)</span>
            </label>
            <p className="form-helper-text">
              Collaborators can add, edit, and remove places from this list. You remain the owner with sole ability to delete the list, change visibility, or transfer ownership.
            </p>

            {/* Selected collaborators */}
            {selectedCollaborators.length > 0 && (
              <div className="collaborator-list">
                {selectedCollaborators.map((friend) => (
                  <div key={friend.id} className="collaborator-item">
                    <div className="collaborator-info">
                      <div className={`collaborator-avatar ${friend.isPremium || friend.subscriptionTier === 'premium' ? 'premium-avatar' : ''}`}>
                        {friend.profilePicture ? (
                          <img src={friend.profilePicture} alt="" className="collaborator-photo" />
                        ) : (
                          <span>{(friend.displayName || friend.username || 'U').charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="collaborator-name">
                        <span className="collaborator-display-name">{friend.displayName || friend.username}</span>
                        {friend.username && (
                          <span className="collaborator-username">@{friend.username}</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-remove-collaborator"
                      onClick={() => handleRemoveCollaborator(friend.id)}
                      title="Remove collaborator"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add collaborator picker */}
            {formData.collaborators.length < MAX_COLLABORATORS && (
              <div className="collaborator-picker" ref={collaboratorPickerRef}>
                <input
                  type="text"
                  className="collaborator-search-input"
                  placeholder="Add a friend as collaborator..."
                  value={collaboratorSearch}
                  onChange={(e) => {
                    setCollaboratorSearch(e.target.value);
                    setShowCollaboratorDropdown(true);
                  }}
                  onFocus={() => setShowCollaboratorDropdown(true)}
                />
                {showCollaboratorDropdown && (
                  <div className="collaborator-dropdown">
                    {loadingFriends ? (
                      <div className="collaborator-dropdown-message">Loading friends...</div>
                    ) : friends.length === 0 ? (
                      <div className="collaborator-dropdown-message">You don't have any friends yet.</div>
                    ) : filteredCollaboratorFriends.length === 0 ? (
                      <div className="collaborator-dropdown-message">No friends match your search</div>
                    ) : (
                      filteredCollaboratorFriends.map((friend) => (
                        <button
                          key={friend.id}
                          type="button"
                          className="collaborator-dropdown-item"
                          onClick={() => handleAddCollaborator(friend)}
                        >
                          <span className="collaborator-dropdown-name">{friend.displayName || friend.username}</span>
                          <span className="collaborator-dropdown-username">@{friend.username}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Collections Picker - Premium Feature */}
          <CollectionsPicker
            canAccessCollections={canAccessCollections}
            selectedCollectionIds={selectedCollectionIds}
            onToggleCollection={handleToggleCollection}
            onClose={() => navigate('/lists')}
          />

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create List'}
            </button>
          </div>
        </form>
      </div>

      <LimitReachedModal
        isOpen={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        type="lists"
        limit={limitModalLimit}
      />
    </div>
  );
}

export default NewList;
